import { instantOf } from '../freshness.js'
import { chartStyle, teamFor, toneStyle } from './colors.js'
import { FantasyChips } from './Fantasy.jsx'
import { CardFloor } from './Floor.jsx'
import { cardFloor, cardStrip, cardStripAge, liveCallout } from './funModel.js'
import { Callout, WinStrip } from './LiveBits.jsx'
import { TeamMark } from './Marks.jsx'
import { lastMetText, sideLeaders, sourceAge, statusText, tvText, teamSub } from './model.js'
import { clockTime, tipTime, zoneName } from './time.js'

// One game on tonight's board. P6-328b (GN-1b).
//
// Four states, drawn from the same markup: PREGAME (the tip, the TV), LIVE (the score and the
// stage of the game), STALE (a live game the page has not heard about for two minutes — what it
// last knew, when it stopped, and nothing that says why) and FINAL (the score and each side's
// leaders: three a side since P6-343, the top player in each of points, rebounds and assists, as the design draws them;
// the one top scorer is the fallback). The state is decided by `model.js::cardKind` and passed in, so a card never
// re-derives it and the headline above it cannot disagree with it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A PREGAME CARD DOES NOT OPEN, AND IT DOES NOT PRETEND TO
// ═══════════════════════════════════════════════════════════════════════════
//
// The bot publishes a game's own object only once its box score says the game has begun, so
// before the tip there is no box score and no plays and a link would lead nowhere. The open
// control is not rendered at all for a pregame card — no disabled button, no dead link, no
// handler on the card — and `GameCard.jsx` is the only place that decides it.
//
// ⚠️ THE WHOLE CARD IS THE TARGET, BUT THE CONTROL IS A REAL BUTTON. The design makes the card
// itself clickable; the button's own pseudo-element stretches over the card (`app.css`), so a
// tap anywhere opens it and the keyboard and a screen reader still get one honest control with a
// name, instead of a click handler on an `article`.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 TWO COLOURS, THE PRIMARY'S, AND EMBER IS NOT ONE OF THEM
// ═══════════════════════════════════════════════════════════════════════════
//
// The card sets `--hoops-away-*` and `--hoops-home-*` from each team's PRIMARY (`colors.js`) and
// the stylesheet does the rest. Nothing here is "you", so nothing here is ember (the member's own
// colour on this site), and a side whose team is not in the table sets nothing and is drawn
// neutral.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE FUN LAYER: A LIVE CARD'S STRIP, CALLOUT AND FIVE (P6-330b, GN-3b)
// ═══════════════════════════════════════════════════════════════════════════
//
// Under the teams, in the design's order: the win-probability strip, the latest callout, and from 720px the five on
// the floor. All three belong to a game being played (`status` live, which a stale card still is: what it last knew,
// drawn as of then), and each is drawn only if it exists — `funModel.js` answers `null` and nothing here is drawn for
// it: no strip in preseason (ESPN publishes no curve), no callout older than four game minutes, no floor on a card
// whose box score is unread, and no heading over an empty row. A FINAL card carries the strip and the callout too,
// frozen with the game, and does not show them; it carries no `on_floor` at all.
//
// ⚠️ THE STRIP IS ESPN'S AND THE CARD'S AGE IS THE NBA'S (P6-334, GN-7). `NBA · 14s` above says how old the SCORE is; the
// strip says how old ITS number is, in a line of its own under the bar, but only once ESPN's own stamp
// (`card.win_probability.as_of`, never the card's `as_of`) is more than two minutes behind the page's clock. While it is
// fresh the strip is exactly what it was. `funModel.js::cardStripAge` decides.
//
// ⚠️ `floorWide` IS TONIGHT'S ONE `matchMedia` QUESTION, asked once by `TonightView.jsx` and handed to every card,
// rather than a listener per card. Below 720px the card has no floor, and it is not hidden with CSS: the five a side
// would be in the document twice over for a screen reader, ten faces a card, on a board of a dozen.

function Side({ side, team, kind, showScore, trailing, which }) {
  const score = Number.isInteger(side.score) ? side.score : null
  const sub = teamSub(side, kind)
  return (
    <div className="d2d-hoops__team" data-side={which} data-testid="hoops-card-team">
      <TeamMark team={team} tricode={side.tricode} which={which} />
      <div className="d2d-hoops__teamtext">
        <div className="d2d-hoops__teamname">{side.name ?? side.tricode}</div>
        {sub === '' ? null : <div className="d2d-hoops__teamsub d2d-micro">{sub}</div>}
      </div>
      {showScore && score !== null ? (
        <span className="d2d-hoops__score" data-trailing={trailing ? 'true' : 'false'}>
          {score}
        </span>
      ) : null}
    </div>
  )
}

export default function GameCard({ entry, teams, slateDate, nowMs, timeZone, onOpen, floorWide = false, mine = [] }) {
  const { card, kind, stampMs } = entry
  const awayTeam = teamFor(teams, card.away?.tricode)
  const homeTeam = teamFor(teams, card.home?.tricode)
  const strip = cardStrip(card)
  const callout = liveCallout(card)
  const floor = floorWide ? cardFloor(card) : null
  const showScore = kind !== 'pregame'
  const awayScore = Number.isInteger(card.away?.score) ? card.away.score : null
  const homeScore = Number.isInteger(card.home?.score) ? card.home.score : null
  const trailing = (mine, other) => mine !== null && other !== null && mine < other

  const tip = instantOf(card.tip_utc)
  const tv = tvText(card)
  const leaders = kind === 'final' ? [sideLeaders(card.away), sideLeaders(card.home)] : []
  const hasLeaders = leaders.some((cells) => cells.length > 0)
  const lastMet = kind === 'pregame' ? lastMetText(card.last_met) : null
  const name = (side) => `${side?.city ?? side?.tricode ?? ''} ${side?.name ?? ''}`.trim()
  const teamsLabel = `${name(card.away)} at ${name(card.home)}`
  const openable = kind !== 'pregame' && typeof onOpen === 'function'

  return (
    <article
      className="d2d-hoops__card"
      data-testid="hoops-card"
      data-state={kind}
      data-game-id={card.id}
      aria-label={teamsLabel}
      style={{ ...toneStyle(awayTeam, homeTeam), ...chartStyle(awayTeam, homeTeam) }}
    >
      <div className="d2d-hoops__cardhead">
        {kind === 'live' ? (
          <span className="d2d-hoops__dot d2d-hoops__dot--live" aria-hidden="true" />
        ) : null}
        {kind === 'stale' ? (
          <span className="d2d-hoops__dot d2d-hoops__dot--stale" aria-hidden="true" />
        ) : null}
        <span className="d2d-hoops__status d2d-micro" data-testid="hoops-card-status">
          {statusText(card, kind, nowMs)}
        </span>
        {card.label ? <span className="d2d-hoops__chip d2d-micro">{card.label}</span> : null}
        <span
          className="d2d-hoops__src d2d-micro"
          data-testid="hoops-card-source"
          title={typeof card.as_of === 'string' ? card.as_of : undefined}
        >
          {sourceAge(card.source, stampMs, nowMs)}
        </span>
      </div>

      <div className="d2d-hoops__teams">
        <Side
          side={card.away}
          team={awayTeam}
          kind={kind}
          which="away"
          showScore={showScore}
          trailing={trailing(awayScore, homeScore)}
        />
        <Side
          side={card.home}
          team={homeTeam}
          kind={kind}
          which="home"
          showScore={showScore}
          trailing={trailing(homeScore, awayScore)}
        />
      </div>

      {kind === 'pregame' && (tip !== null || tv !== null || lastMet !== null) ? (
        <div className="d2d-hoops__pre" data-testid="hoops-card-pre">
          {tip === null ? null : (
            <div className="d2d-hoops__tip">
              <span className="d2d-hoops__tiptime" data-testid="hoops-card-tip">
                {tipTime(tip, slateDate, timeZone)}
              </span>
              <span className="d2d-hoops__tipzone d2d-micro">{zoneName(tip, timeZone)}</span>
            </div>
          )}
          {tv === null ? null : (
            <div className="d2d-hoops__row">
              <span className="d2d-hoops__rowlabel d2d-micro">TV</span>
              <span className="d2d-hoops__rowvalue" data-testid="hoops-card-tv">
                {tv}
              </span>
            </div>
          )}
          {lastMet === null ? null : (
            <div className="d2d-hoops__row">
              <span className="d2d-hoops__rowlabel d2d-micro">Last met</span>
              <span className="d2d-hoops__rowvalue" data-testid="hoops-card-lastmet">
                {lastMet}
              </span>
            </div>
          )}
        </div>
      ) : null}

      {strip === null ? null : (
        <WinStrip
          strip={strip}
          away={card.away?.tricode}
          home={card.home?.tricode}
          testId="hoops-card-wp"
          age={cardStripAge(card, nowMs)}
        />
      )}
      {callout === null ? null : <Callout callout={callout} testId="hoops-card-callout" />}
      {floor === null ? null : (
        <CardFloor
          floor={floor}
          sides={{
            away: { tricode: card.away?.tricode },
            home: { tricode: card.home?.tricode },
          }}
        />
      )}

      {/* P6-333a (GN-6a): the viewer's own players on this game, only when they have a league (HoopsPanel passes `mine`
          from their blob, and a member without one is passed nothing). A stale card says the figures are as of its stamp. */}
      <FantasyChips
        chips={mine}
        asOf={kind === 'stale' && stampMs !== null ? clockTime(stampMs, timeZone) : null}
        testId="hoops-card-mine"
      />

      {kind === 'stale' && stampMs !== null ? (
        // 🔴 ONE SENTENCE, NO ERROR TEXT AND NO CHIP. It says when the page stopped hearing and that
        // what is above is as of then. It does not say a feed is down, because the page does not
        // know that: it knows only how old its last answer is.
        <p className="d2d-hoops__stalenote" data-testid="hoops-card-stale">
          Stopped updating at {clockTime(stampMs, timeZone)}. Score, clock and lines are as of then.
        </p>
      ) : null}

      {kind === 'final' && hasLeaders ? (
        <div className="d2d-hoops__leaders" data-testid="hoops-card-leaders">
          <span className="d2d-hoops__rowlabel d2d-micro">Leaders</span>
          {[
            ['away', card.away, leaders[0]],
            ['home', card.home, leaders[1]],
          ].map(([which, side, cells]) =>
            cells.length === 0 ? null : (
              <div className="d2d-hoops__leader" key={which}>
                <span className="d2d-hoops__leaderabbr" data-side={which}>
                  {side.tricode}
                </span>
                <span className="d2d-hoops__leadertext">
                  {cells.map((cell) => (
                    <span className="d2d-hoops__leadercell" data-stat={cell.key} key={cell.key}>
                      <span className="d2d-hoops__leadername">{cell.name}</span>{' '}
                      <span className="d2d-hoops__leaderfig">{cell.figure}</span>
                    </span>
                  ))}
                </span>
              </div>
            ),
          )}
        </div>
      ) : null}

      {openable ? (
        <button
          type="button"
          className="d2d-hoops__open"
          data-testid="hoops-card-open"
          aria-label={`Open game: ${teamsLabel}`}
          onClick={() => onOpen(card.id)}
        >
          <span>Open game</span>
          <span aria-hidden="true">→</span>
        </button>
      ) : null}
    </article>
  )
}
